import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { BackButton } from "@/components/BackButton";
import { PageTransition } from "@/components/PageTransition";
import { useRecoverWithSlip } from "@/hooks/api/useRecoverWithSlip";
import { useGetFrames } from "@/hooks/api/useGetFrames";
import { track } from "@/lib/analytics/track";
import { usePhotoHistoryStore } from "@/lib/store/photoHistoryStore";
import { usePaymentStore } from "@/lib/store/paymentStore";
import { useFrameStore } from "@/lib/store/frameStore";
import { useSEO } from "@/lib/seo/useSEO";
import { ApiError } from "@/api";
import type {
  RecoverPaymentFoundNoPhotosResponseDto,
  RecoverPhotoFoundResponseDto,
} from "@/api";

const MAX_SLIP_BYTES = 2 * 1024 * 1024;
const OVERSIZED_COPY = "Please upload the slip as a JPEG or PNG image under 2 MB.";
const FALLBACK_ERROR_COPY = "Something went wrong. Please try again.";
const FRAME_UNAVAILABLE_COPY =
  "The frame you paid for is no longer available. Please contact support.";

type RecoverResult =
  | RecoverPhotoFoundResponseDto
  | RecoverPaymentFoundNoPhotosResponseDto;

export const RecoverPage: React.FC = () => {
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useRecoverWithSlip();
  const { data: frames, refetch } = useGetFrames();
  const addEntry = usePhotoHistoryStore((state) => state.addEntry);
  const { setPaymentPayload } = usePaymentStore();
  const { setFrame } = useFrameStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);

  // StrictMode double-invokes effects (setup → cleanup → setup) in dev;
  // useRef(true) only initializes on the first mount, so re-arm here or the
  // ref stays false for the whole life of the page.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [frameUnavailable, setFrameUnavailable] = useState(false);

  useSEO({
    title: "Find My Photo - Recover Your Session",
    description:
      "Upload your bank payment slip and we'll find your photo booth session.",
    keywords: "recover photo, find my photo, payment slip, KeptScene",
  });

  const resetToIdle = () => {
    setErrorMessage(null);
    setFrameUnavailable(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSuccess = async (data: RecoverResult) => {
    if (data.outcome === "PHOTO_FOUND") {
      track("recover_slip_found", { job_id_present: true });
      addEntry({
        jobId: data.jobId,
        createdAt: new Date().toISOString(),
      });
      navigate(`/result?jobId=${data.jobId}`);
      return;
    }

    track("recover_slip_no_photo", { child_frame_id_present: true });
    let frame = frames?.find((f) => f.childFrameId === data.childFrameId);
    if (!frame) {
      const fresh = await refetch();
      if (!mountedRef.current) return;
      frame = fresh.data?.find((f) => f.childFrameId === data.childFrameId);
    }
    if (!frame) {
      setFrameUnavailable(true);
      return;
    }
    // The paid childFrameId is the only frame authority — always overwrite
    // whatever the user had selected.
    setFrame(frame);
    setPaymentPayload({
      token: data.token,
      transactionId: data.transactionId,
      childFrameId: data.childFrameId,
    });
    navigate("/camera");
  };

  const handleError = (error: unknown) => {
    if (error instanceof ApiError) {
      const code = error.body?.error?.code;
      if (code === "SLIP_UNREADABLE") track("recover_slip_unreadable", {});
      else if (code === "SLIP_NOT_MATCHED") track("recover_slip_not_found", {});
      setErrorMessage(error.body?.error?.userMessage ?? FALLBACK_ERROR_COPY);
      return;
    }
    setErrorMessage(FALLBACK_ERROR_COPY);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_SLIP_BYTES) {
      toast.error(OVERSIZED_COPY);
      return;
    }
    setErrorMessage(null);
    setFrameUnavailable(false);
    track("recover_slip_upload_start", { slip_present: true });
    try {
      const data = await mutateAsync({ slip: file });
      if (!mountedRef.current) return;
      await handleSuccess(data);
    } catch (error) {
      if (!mountedRef.current) return;
      handleError(error);
    }
  };

  return (
    <PageTransition className="min-h-screen overflow-y-hidden">
      <BackButton prevUrl="/" />
      <div className="px-8 py-6">
        <div className="bg-[url('/tape.png')] bg-cover bg-center w-full text-center -rotate-6">
          <h1 className="text-[40px] font-family-reenie text-vintage-black mb-8">
            FIND MY PHOTO
          </h1>
        </div>
        <p className="text-center font-family-reenie text-vintage-black text-[24px]">
          Upload your bank payment slip and we&apos;ll find your photos.
        </p>
        <p className="text-center font-family-kanit text-xs text-[#6f655c] mt-2">
          Coupon payments can&apos;t be recovered by slip.
        </p>

        <div className="mt-6 flex flex-col items-center gap-4">
          {isPending ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-8 h-8 animate-spin text-vintage-red" />
              <p className="font-family-kanit text-sm text-vintage-black">
                Searching for your payment…
              </p>
            </div>
          ) : (
            <>
              <label
                htmlFor="recover-slip-upload"
                className="inline-flex items-center justify-center gap-2 min-h-[44px] px-[18px] py-2.5 border border-vintage-black bg-vintage-red text-white font-family-kanit font-bold text-sm cursor-pointer"
              >
                <Upload className="w-4 h-4" />
                Upload payment slip
              </label>
              <input
                ref={fileInputRef}
                id="recover-slip-upload"
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={handleFileChange}
              />
            </>
          )}

          {errorMessage && (
            <div className="border border-vintage-black bg-vintage-pink p-[18px] text-center w-full">
              <p className="font-family-kanit text-sm text-vintage-black">
                {errorMessage}
              </p>
              <button
                type="button"
                onClick={resetToIdle}
                className="inline-flex items-center justify-center min-h-[44px] px-[18px] py-2.5 mt-3 border border-vintage-black bg-vintage-red text-white font-family-kanit font-bold text-sm cursor-pointer"
              >
                Try again
              </button>
            </div>
          )}

          {frameUnavailable && (
            <div className="border border-vintage-black bg-vintage-pink p-[18px] text-center w-full">
              <p className="font-family-kanit text-sm text-vintage-black">
                {FRAME_UNAVAILABLE_COPY}
              </p>
            </div>
          )}
        </div>
      </div>
    </PageTransition>
  );
};
